import { AxiosResponse } from "axios";
import classNames from "classnames";
import { Form, Formik, useFormikContext } from "formik";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";

import AsyncSearchableSelect from "components/AsyncSearchableSelect";
import Button from "components/Button";
import ImageUpload, { ImageFile } from "components/ImageUpload";
import Textarea from "components/Textarea";
import TextInput from "components/TextInput";

import useValidation from "hooks/useValidation";

import { validationSchema } from "../validationSchema";

import { CompaniesApi, UsersApi } from "services/http";
import { ALLOWED_IMAGE_TYPES, MAX_IMAGE_SIZE, MAX_PORTFOLIO_IMAGES, QUERY_PARAMS } from "utils/constants";
import { QueryHelper } from "utils/helpers";

import { BaseOption, Meta, UserRole } from "entities/common";
import { UserItem } from "entities/companies";
import { RootStore } from "store";
import { useAppSelector } from "store/hooks";

import styles from "../styles.module.scss";

export type PortfolioFormValues = {
  name: string;
  companyId: string;
  description: string;
  city: string;
  area: string;
  buildingName: string;
  unitNumber: string;
  street: string;
  managerId: string;
  images: ImageFile[];
};

type PropTypes = {
  onClose?: () => void;
  onSubmit?: (values: PortfolioFormValues) => void;
  mode?: "create" | "edit";
  description?: string;
  initialData?: PortfolioFormValues;
  isShowCompanySelect?: boolean;
  companyId?: number;
  isLoading: boolean;
};

type FormFieldsProps = {
  onCancel: () => void;
  mode: "create" | "edit";
  isShowCompanySelect?: boolean;
  companyId?: number;
  isLoading: boolean;
};

const FormFields: FC<FormFieldsProps> = ({ onCancel, mode, isShowCompanySelect, companyId, isLoading }) => {
  const formRef = useRef<HTMLDivElement>(null);
  const { values, errors, touched, setFieldValue, handleChange, handleBlur, handleSubmit, setErrors } =
    useFormikContext<PortfolioFormValues>();
  const [companyLabel, setCompanyLabel] = useState<string>("");
  const [managerLabel, setManagerLabel] = useState<string>("");

  const showCompany = isShowCompanySelect && mode === "create";

  const authUser = useAppSelector((state: RootStore) => state.auth.user);
  const portfolioErrors = useAppSelector((state: RootStore) => state.portfolios.errors);
  const role = useMemo(() => authUser?.role, [authUser?.role]);
  const authCompanyId = useMemo(() => authUser?.company_id, [authUser?.company_id]);

  useValidation(formRef, errors);

  const handleCompanySearch = useCallback(async (search: string, page: number) => {
    try {
      const { data } = await CompaniesApi.getList(
        QueryHelper.getQueryString({
          [QUERY_PARAMS.SEARCH]: search || undefined,
          [QUERY_PARAMS.PAGE]: page,
          [QUERY_PARAMS.PAGE_SIZE]: 50,
        }),
      );

      const hasMore = page * 50 < data?.total;
      const options = data?.items?.map((company) => ({ value: String(company?.id), label: company?.name }));

      return { options, hasMore };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const handleCompanyChange = useCallback(
    (value: string, label: string) => {
      setFieldValue("companyId", value);
      setCompanyLabel(label);
      setFieldValue("managerId", "");
      setManagerLabel("");
      setFieldValue("city", "");
    },
    [setFieldValue],
  );

  const effectiveCompanyId = Number(values.companyId) || companyId || authCompanyId;

  const handleCitySearch = useCallback(
    async (search: string): Promise<{ options: BaseOption[]; hasMore: boolean }> => {
      if (!effectiveCompanyId) {
        return { options: [], hasMore: false };
      }

      try {
        const { data } = await CompaniesApi.getEmirateCities({
          company_id: effectiveCompanyId,
          search: search || undefined,
        });
        const options = (data?.items || []).map((item) => ({ value: item.name, label: item.name }));

        return { options, hasMore: false };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [effectiveCompanyId],
  );

  const handleCityChange = useCallback(
    (value: string) => {
      setFieldValue("city", value);
    },
    [setFieldValue],
  );

  const handleManagerSearch = useCallback(
    async (search: string, page: number) => {
      try {
        const currentCompanyId = Number(values.companyId) || companyId || authCompanyId;
        let data: ({ items: UserItem[] } & Meta) | undefined;

        if (currentCompanyId) {
          if (role === UserRole.ADMINISTRATOR) {
            data = await CompaniesApi.getCompanyUsers({
              companyId: currentCompanyId,
              query: QueryHelper.getQueryString({
                [QUERY_PARAMS.SEARCH]: search || undefined,
                [QUERY_PARAMS.PAGE]: page,
                [QUERY_PARAMS.PAGE_SIZE]: 50,
              }),
            }).then((res: AxiosResponse<{ items: UserItem[] } & Meta>) => res.data);

            return {
              options: (data?.items || [])?.map((user) => ({ value: String(user?.id), label: user?.full_name })) || [],
              hasMore: data?.total ? page * 50 < data?.total : false,
            };
          } else {
            data = await UsersApi.getList(
              QueryHelper.getQueryString({
                [QUERY_PARAMS.SEARCH]: search || undefined,
                [QUERY_PARAMS.PAGE]: page,
                [QUERY_PARAMS.PAGE_SIZE]: 50,
                [QUERY_PARAMS.COMPANY_ID]: currentCompanyId,
              }),
            )?.then((res: AxiosResponse<{ items: UserItem[] } & Meta>) => res.data);

            return {
              options: (data?.items || [])?.map((user) => ({ value: String(user?.id), label: user?.full_name })),
              hasMore: data?.total ? page * 50 < data?.total : false,
            };
          }
        } else {
          if (role === UserRole.ADMINISTRATOR) {
            data = await UsersApi.getList(
              QueryHelper.getQueryString({
                [QUERY_PARAMS.SEARCH]: search || undefined,
                [QUERY_PARAMS.PAGE]: page,
                [QUERY_PARAMS.PAGE_SIZE]: 50,
              }),
            )?.then((res: AxiosResponse<{ items: UserItem[] } & Meta>) => res.data);

            return {
              options: (data?.items || [])?.map((user) => ({ value: String(user?.id), label: user?.full_name })),
              hasMore: data?.total ? page * 50 < data?.total : false,
            };
          }

          return { options: [], hasMore: false };
        }
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [authCompanyId, companyId, role, values.companyId],
  );

  const handleManagerChange = useCallback(
    (value: string, label: string) => {
      setFieldValue("managerId", value);
      setManagerLabel(label);
    },
    [setFieldValue],
  );

  useEffect(() => {
    if (portfolioErrors) {
      setErrors({
        ...(portfolioErrors?.detail?.portfolio_name && { name: portfolioErrors.detail.portfolio_name }),
        ...(portfolioErrors?.detail?.company && { company: portfolioErrors.detail.company }),
        ...(portfolioErrors?.detail?.description && { description: portfolioErrors.detail.description }),
        ...(portfolioErrors?.detail?.city && { city: portfolioErrors.detail.city }),
        ...(portfolioErrors?.detail?.area && { area: portfolioErrors.detail.area }),
        ...(portfolioErrors?.detail?.building_name && { buildingName: portfolioErrors.detail.building_name }),
        ...(portfolioErrors?.detail?.unit_number && { unitNumber: portfolioErrors.detail.unit_number }),
        ...(portfolioErrors?.detail?.street && { street: portfolioErrors.detail.street }),
        ...(portfolioErrors?.detail?.manager && { manager: portfolioErrors.detail.manager }),
        ...(portfolioErrors?.detail?.images && { images: portfolioErrors.detail.images }),
      });
    }
  }, [portfolioErrors]);

  return (
    <div ref={formRef} className={styles.content}>
      <Form onSubmit={handleSubmit}>
        <div className={styles.content}>
          <fieldset className={styles.fieldset}>
            <p className={styles.fieldsetTitle}>Basic information</p>
            <div className={styles.fieldsetFields}>
              <div className={classNames(styles.basicInfoRow, { [styles.showCompanySelect]: showCompany })}>
                <TextInput
                  id="name"
                  name="name"
                  label="Portfolio name"
                  placeholder="Portfolio name"
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.name ? errors.name : undefined}
                  isRequired
                  isDisabled={isLoading}
                  maxLength={100}
                />

                {showCompany && (
                  <AsyncSearchableSelect
                    label="Company"
                    placeholder="Select company"
                    value={values.companyId}
                    selectedLabel={companyLabel}
                    onSearch={handleCompanySearch}
                    onChange={handleCompanyChange}
                    error={touched.companyId ? errors.companyId : undefined}
                    isRequired
                    isDisabled={isLoading}
                  />
                )}
              </div>
              <div className={styles.description}>
                <Textarea
                  id="description"
                  name="description"
                  label="Description"
                  placeholder="Brief description of the portfolio..."
                  value={values.description}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  rows={4}
                  isDisabled={isLoading}
                  maxLength={500}
                  error={errors.description}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className={styles.fieldset}>
            <p className={styles.fieldsetTitle}>Address</p>
            <div className={styles.fieldsetFields}>
              <AsyncSearchableSelect
                label="City"
                placeholder="Select city"
                value={values.city}
                selectedLabel={values.city}
                onSearch={handleCitySearch}
                onChange={handleCityChange}
                error={touched.city ? errors.city : undefined}
                isRequired
                isDisabled={isLoading || !effectiveCompanyId}
              />

              <div className={styles.addressRow}>
                <TextInput
                  id="area"
                  name="area"
                  label="Area"
                  placeholder="Area"
                  value={values.area}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.area ? errors.area : undefined}
                  isRequired
                  isDisabled={isLoading}
                  maxLength={100}
                />

                <TextInput
                  id="buildingName"
                  name="buildingName"
                  label="Building name"
                  placeholder="Building name"
                  value={values.buildingName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.buildingName ? errors.buildingName : undefined}
                  isDisabled={isLoading}
                  maxLength={100}
                />
              </div>

              <div className={styles.addressRow}>
                <TextInput
                  id="unitNumber"
                  name="unitNumber"
                  label="Unit number"
                  placeholder="Unit number"
                  value={values.unitNumber}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.unitNumber ? errors.unitNumber : undefined}
                  isRequired
                  isDisabled={isLoading}
                  maxLength={50}
                />

                <TextInput
                  id="street"
                  name="street"
                  label="Street"
                  placeholder="Street"
                  value={values.street}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.street ? errors.street : undefined}
                  isDisabled={isLoading}
                  maxLength={150}
                />
              </div>
            </div>
          </fieldset>

          <fieldset className={styles.fieldset}>
            <p className={styles.fieldsetTitle}>Responsible person</p>
            <div className={styles.fieldsetFields}>
              <AsyncSearchableSelect
                label="Portfolio manager"
                placeholder="Select manager"
                value={values.managerId}
                selectedLabel={managerLabel}
                onSearch={handleManagerSearch}
                onChange={handleManagerChange}
                error={touched.managerId ? errors.managerId : undefined}
                isRequired
                isDisabled={isLoading}
              />

              <div className={styles.inputHint}>
                <span>!</span>
                <span>
                  This is the person who will receive notifications if incidents occur in any asset in the portfolio
                </span>
              </div>
            </div>
          </fieldset>

          <fieldset className={styles.fieldset}>
            <p className={styles.fieldsetTitle}>Portfolio image</p>
            <ImageUpload
              images={values.images}
              onChange={(imgs) => setFieldValue("images", imgs)}
              maxFiles={MAX_PORTFOLIO_IMAGES}
              maxSize={MAX_IMAGE_SIZE}
              allowedTypes={ALLOWED_IMAGE_TYPES}
            />
          </fieldset>

          <div className={styles.buttons}>
            <Button type="button" variant="secondary" onClick={onCancel}>
              Cancel
            </Button>

            <Button type="submit" variant="primary">
              {mode === "create" ? "Create" : "Update"}
            </Button>
          </div>
        </div>
      </Form>
    </div>
  );
};

const defaultInitialValues: PortfolioFormValues = {
  name: "",
  companyId: "",
  description: "",
  city: "",
  area: "",
  buildingName: "",
  unitNumber: "",
  street: "",
  managerId: "",
  images: [],
};

const ModalCreatePortfolioContent: FC<PropTypes> = ({
  onClose,
  onSubmit,
  mode = "create",
  description,
  initialData,
  isShowCompanySelect,
  companyId,
  isLoading,
}) => {
  const initialValues = initialData || defaultInitialValues;

  const handleCancel = () => {
    onClose?.();
  };

  return (
    <>
      {!!description && <p className={styles.modalDescription}>{description}</p>}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema(isShowCompanySelect || false)}
        onSubmit={(values) => {
          if (isLoading) {
            return;
          }

          onSubmit?.(values);
        }}
        enableReinitialize
      >
        <FormFields
          onCancel={handleCancel}
          mode={mode}
          isShowCompanySelect={isShowCompanySelect}
          companyId={companyId}
          isLoading={isLoading}
        />
      </Formik>
    </>
  );
};

export default ModalCreatePortfolioContent;
